import React from 'react';
import { Button } from 'antd';
import { CarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import SupplierOrderTruckFreightList from '../sections/SupplierOrderTruckFreightList';
import SupplierOrderDeliveryTransportsSection from '../sections/SupplierOrderDeliveryTransportsSection';
import type { SupplierOrderTransportDto } from '../../../dto/transport';
import { formatCurrency } from '../../../utils/currency';
import { getTransportLabel } from '../../../utils/transportLabel';
import type { SupplierOrderStageInvoice } from './useSupplierOrderStageData';
import type { ProductDimensionLine } from '../../../components/products/ProductNameWithDimensions';

interface SupplierOrderDeliveryAsideProps {
  supplierOrderId: string;
  supplierOrderLabel: string;
  loadingPlace: string | null;
  canPlanTransports: boolean;
  canViewCosts: boolean;
  fullyOnTrucks: boolean;
  onOpenTransport: (transportId: string) => void;
  onTransportsChanged?: () => void;
  onTransportsLoaded: (transports: SupplierOrderTransportDto[] | null | undefined) => void;
  /** Trucks of this ZD as the section loaded them (undefined = loading, null = failed). */
  transports: SupplierOrderTransportDto[] | null | undefined;
  /** Sum of this ZD's shares of its trucks in PLN (null = no freight known yet; undefined = not visible). */
  freightPLN?: number | null;
  /** Numbers of the received PZ documents (null = unknown). */
  pzNumbers: string[] | null;
  /** Numbers of the approved purchase invoices (null = unknown or not visible). */
  invoiceNumbers: string[] | null;
  /**
   * Approved purchase invoices with ids. With `onOpenInvoice` each number opens
   * the invoice window on this card — the only place goods are received into a
   * PZ (decision 2026-10-14), also after „Nie, później” or for a later truck.
   */
  invoices?: SupplierOrderStageInvoice[] | null;
  onOpenInvoice?: (invoiceId: string) => void;
  /** ZD lines of the card: dimensions under the product names of the freight lines. */
  items?: ProductDimensionLine[];
}

/**
 * Side card „Dostawa” of the stage panel (makieta `renderPlan`): the truck of
 * this ZD, the direction, the freight, the received PZ documents and the
 * purchase invoice — and the actions to plan a truck to our warehouse or
 * straight to the customer („prosto z fabryki”) and to add the ZD to an
 * existing truck. The list of trucks itself is the strip in the card header.
 */
export default function SupplierOrderDeliveryAside({
  supplierOrderId,
  supplierOrderLabel,
  loadingPlace,
  canPlanTransports,
  canViewCosts,
  fullyOnTrucks,
  onOpenTransport,
  onTransportsLoaded,
  onTransportsChanged,
  transports,
  freightPLN,
  pzNumbers,
  invoiceNumbers,
  invoices,
  items,
  onOpenInvoice,
}: SupplierOrderDeliveryAsideProps) {
  const { t } = useTranslation(['orders']);
  const fact = (label: string, value: string, testId: string) => (
    <div className="zd-fact" data-testid={testId}>
      <span>{label}</span>
      <b title={value}>{value}</b>
    </div>
  );
  const first = transports && transports.length ? transports[0] : null;
  // Rodzaj of the trucks (do magazynu / bezpośrednio z fabryki do klienta / z magazynu), not a direction.
  const kinds = Array.from(new Set((transports ?? []).map((transport) => transport.kind).filter(Boolean)));
  const kindsText = kinds.length
    ? kinds.map((kind) => t(`orders:supplierOrders.card.header.trucksGroup.${kind}`)).join(', ')
    : t('orders:supplierOrders.card.delivery.directionNone');
  return (
    <div data-testid="supplier-order-delivery-transports" className="zd-aside">
      <div className="zd-aside__title">{t('orders:supplierOrders.card.delivery.title')}</div>
      {/* Two equal tiles: what the delivery is (left) and what can be planned (right). */}
      <div className={`zd-aside__grid${canPlanTransports ? ' has-plan' : ''}`}>
        <div className="zd-aside__tile" data-testid="supplier-order-delivery-state">
          <div className={`zd-aside__truck${first ? '' : ' is-empty'}`} data-testid="supplier-order-delivery-truck">
            {first ? (
              <>
                <CarOutlined aria-hidden="true" />
                <span>{getTransportLabel(first)}{transports && transports.length > 1 ? ` +${transports.length - 1}` : ''}</span>
              </>
            ) : t('orders:supplierOrders.card.delivery.noTruck')}
          </div>
          {fact(t('orders:supplierOrders.card.delivery.direction'), kindsText, 'supplier-order-delivery-direction')}
          {freightPLN !== undefined && fact(
            t('orders:supplierOrders.card.delivery.freight'),
            freightPLN === null
              ? (first ? t('orders:supplierOrders.card.delivery.freightUnknown') : '—')
              : t('orders:supplierOrders.card.delivery.freightInBatch', { amount: formatCurrency(freightPLN, 'PLN') }),
            'supplier-order-delivery-freight',
          )}
          {canViewCosts && transports ? <SupplierOrderTruckFreightList compact transports={transports} onOpenTransport={onOpenTransport} items={items} /> : null}
          {fact(
            t('orders:supplierOrders.card.delivery.receipts'),
            pzNumbers === null ? '—' : (pzNumbers.length ? pzNumbers.join(', ') : t('orders:supplierOrders.card.delivery.receiptsNone')),
            'supplier-order-delivery-receipts',
          )}
          {invoices && invoices.length > 0 && onOpenInvoice ? (
            <div className="zd-fact" data-testid="supplier-order-delivery-invoice">
              <span>{t('orders:supplierOrders.card.delivery.purchaseInvoice')}</span>
              <b>
                {invoices.map((invoice) => (
                  <Button key={invoice.id} type="link" size="small" style={{ padding: 0, height: 'auto', marginRight: 8 }}
                    data-testid="supplier-order-delivery-invoice-open" onClick={() => onOpenInvoice(invoice.id)}>
                    {invoice.number}
                  </Button>
                ))}
              </b>
            </div>
          ) : invoiceNumbers !== null && fact(
            t('orders:supplierOrders.card.delivery.purchaseInvoice'),
            invoiceNumbers.length ? invoiceNumbers.join(', ') : t('orders:supplierOrders.card.delivery.purchaseInvoiceNone'),
            'supplier-order-delivery-invoice',
          )}
        </div>
        {canPlanTransports ? (
        <div className="zd-aside__tile" data-testid="supplier-order-delivery-plan">
          <SupplierOrderDeliveryTransportsSection
            supplierOrderId={supplierOrderId}
            supplierOrderLabel={supplierOrderLabel}
            loadingPlace={loadingPlace}
            canPlanTransports={canPlanTransports}
            canViewCosts={canViewCosts}
            fullyOnTrucks={fullyOnTrucks}
            onOpenTransport={onOpenTransport}
            onLoaded={onTransportsLoaded}
            onChanged={onTransportsChanged}
            hideList
            compact
          />
        </div>
        ) : (
          // Nothing left to plan (goods received): the section still loads the trucks for the card.
          <div hidden>
            <SupplierOrderDeliveryTransportsSection
              supplierOrderId={supplierOrderId}
              supplierOrderLabel={supplierOrderLabel}
              loadingPlace={loadingPlace}
              canPlanTransports={false}
              canViewCosts={canViewCosts}
              fullyOnTrucks={fullyOnTrucks}
              onOpenTransport={onOpenTransport}
              onLoaded={onTransportsLoaded}
            onChanged={onTransportsChanged}
              hideList
              compact
            />
          </div>
        )}
      </div>
    </div>
  );
}
